import { useEffect } from 'react';
import { useMaintenanceState } from '@hooks/useMaintenanceState';
import { useTakeoverFeed } from '@hooks/useTakeoverFeed';
import { getState as getMaintenanceState, maybeProbe } from '@lib/maintenance';
import { amplitude, isAmplitudeInitialized, subscribeAmplitudeReady } from '@lib/amplitude';
import { getContextTypeFromPathname } from '@lib/analytics/contextType';
import { getOrCreateSessionId } from '@lib/analytics/session';
import { useAuthStore } from '@stores/authStore';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
import { MaintenanceState } from './MaintenanceState';
import { ConnectionState } from './ConnectionState';
import { OfflineState } from './OfflineState';

/**
 * Top-level maintenance orchestrator. Reads state and renders the
 * matching sub-component, or returns null when status === 'ready'.
 *
 * "Try again" re-runs the discriminator probe via maybeProbe('manual').
 * The probe writes its result to module state, and the subscribe-driven
 * hook dismisses the takeover when status returns to 'ready'. On native
 * a full reload would cold-start the WebView and lose in-memory state
 * (auth, drafts), so the probe-and-dismiss path is the right call on
 * both platforms.
 *
 * The 'manual' reason bypasses the 10-second debounce window (an
 * impatient user clicking Try Again should not be silently throttled),
 * but maybeProbe's in-flight coalescing still folds rapid repeat clicks
 * into a single probe chain (favicon + /api/maintenance + /health).
 * Calling runProbe() directly here would start a fresh chain on every
 * click - up to 3 fetches per click - exactly when the network is
 * already under stress.
 */
export function MaintenanceTakeover() {
  const { status, customMessage } = useMaintenanceState();
  useTakeoverFeed('maintenance', status !== 'ready');

  // Track when a non-ready takeover is shown so production deployments
  // can monitor maintenance impact in Amplitude. Fire-and-forget; analytics
  // failures must never affect UI rendering.
  //
  // Cold-start path: Amplitude init is deferred (requestIdleCallback / load),
  // so a takeover fired during the very first probe can land before init
  // completes. Without replay-on-init the event would be silently dropped.
  // subscribeAmplitudeReady fires the callback when init completes, or
  // synchronously if init already finished.
  //
  // Only depend on status: a customMessage edit mid-takeover (operator
  // updates the Cloudflare flag) shouldn't fire a duplicate event. If status
  // changes before init completes, the cleanup unsubscribes the old listener
  // and the new effect registers one for the most-recent status, so the
  // tracked event reflects what the user is currently looking at.
  //
  // hasCustomMessage reads from getMaintenanceState() at SEND time, not from
  // the closure: customMessage is intentionally not in deps (so message edits
  // don't re-fire the event), but on the cold-start replay path the listener
  // closure can outlive a customMessage update (init completes after the
  // operator typed a message). Reading the singleton at send time means the
  // replayed event reports the message that was actually on screen when
  // Amplitude came online, not the one captured at mount time.
  useEffect(() => {
    if (status === 'ready') return;
    const send = () => {
      try {
        const currentMessage = getMaintenanceState().customMessage;
        // Enrichment fields mirror `useAmplitudeTracking.getBasePayload()`
        // so maintenance events join the same session / route / auth-state
        // analysis as the rest of the app's analytics. We compute them
        // inline (rather than routing through `trackEvent`) because the
        // hook no-ops in `NODE_ENV === 'test'` — which would erase the
        // takeover_shown coverage in MaintenanceTakeover.test.tsx — and
        // because the hook's `scheduleIdle(1500ms)` deferral can race the
        // takeover unmount in fast-recovery scenarios. The direct
        // `amplitude.track` here keeps both observability paths
        // (production + test) honest.
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        amplitude.track('maintenance_takeover_shown', {
          state: status,
          hasCustomMessage: currentMessage.length > 0,
          session_id: getOrCreateSessionId(),
          path: pathname || null,
          context_type: pathname ? getContextTypeFromPathname(pathname) : 'unknown',
          is_authenticated: useAuthStore.getState().isAuthenticated,
        });
      } catch (error) {
        // logger.error (always logged) plus a Sentry breadcrumb so a
        // broken Amplitude pipeline surfaces in production. logger.debug
        // here was silent in prod, defeating the whole point of measuring
        // maintenance impact.
        // sanitizeError strips Axios `config` (Authorization header) and
        // `request` (raw XHR) before logging — matches the convention
        // used by queryCacheOnError and the new-posts pill detector. The
        // Sentry breadcrumb below already sanitizes inline via
        // `(error as Error)?.message`, so this just closes the last
        // raw-error console.error in this block.
        logger.error('maintenance: failed to track takeover_shown', sanitizeError(error));
        if (typeof window !== 'undefined') {
          try {
            window.Sentry?.addBreadcrumb?.({
              category: 'maintenance',
              message: 'analytics track failed',
              level: 'warning',
              data: {
                state: status,
                error: (error as Error)?.message ?? String(error),
              },
            });
          } catch {
            // Sentry SDK throwing must never break the takeover.
          }
        }
      }
    };
    if (isAmplitudeInitialized()) {
      send();
      return;
    }
    return subscribeAmplitudeReady(send);
  }, [status]);

  const handleTryAgain = () => {
    void maybeProbe('manual');
  };

  switch (status) {
    case 'ready':
      return null;
    case 'maintenance':
      return <MaintenanceState customMessage={customMessage} onTryAgain={handleTryAgain} />;
    case 'connection':
      return <ConnectionState onTryAgain={handleTryAgain} />;
    case 'offline':
      return <OfflineState onTryAgain={handleTryAgain} />;
    default: {
      const _exhaustive: never = status;
      void _exhaustive;
      return null;
    }
  }
}
